/*
 * MathGraph32 Javascript : Software for animating online dynamic mathematics figures
 * http://www.mathgraph32.org/
 * @Author Yves Biton (yves.biton@sesamath.net)
 * @License: GNU AGPLv3 https://www.gnu.org/licenses/agpl-3.0.html
 */
const NatObj = require('../types/NatObj')
const NatCal = require('../types/NatCal')
const Complexe = require('../types/Complexe')
const Dimf = require('../types/Dimf')
const Fonte = require('../types/Fonte')
const Nat = require('../types/Nat')
const Opef = require('../types/Opef')
const Opef2v = require('../types/Opef2v')
const Opef3v = require('../types/Opef3v')
const Opef4v = require('../types/Opef4v')
const Opef5v = require('../types/Opef5v')
const Pointeur = require('../types/Pointeur')
const StyleEncadrement = require('../types/StyleEncadrement')
const kernel = require('../kernel/kernel')
const CCb = require('./CCb')
const CalcR = require('../kernel/CalcR')
const CalcC = require('../kernel/CalcC')
const CAffichageLieAPoint = require('./CAffichageLieAPoint')
module.exports = CEditeurFormule
const CLatex = require('./CLatex')

/**
 * Classe représentant un éditeur de formule permettant à l'utilisateur de modifier
 * la formule d'un calcul sur la figure.
 * On peut associer à l'éditeur une fonction de callBack nommée callBackOK qui
 * sera appelée quand l'utilisateur aura validé sa réponse.
 * @constructor
 * @extends CAffichageLieAPoint
 * @param {CListeObjets} listeProprietaire : La liste propriétaire.
 * @param {CImplementationProto} impProto : null ou la construction propriétaire.
 * @param {Boolean} estElementFinal : true si l'objet est un élément final de construction.
 * @param {mtg32.Color} couleur : La couleur d'éciture de l'éditeur (et du cadre éventuel).
 * @param {Float} x_nom : L'abscisse d'affichage de l'éditeur
 * @param {Float} y_nom : L'ordonnée d'affichage de l'éditeur
 * @param {Integer} dec_x : Décalage horizontal du nom
 * @param {Integer} dec_y : Décalage vertical du nom
 * @param {Boolean} masque : true si l'éditeur est masqué
 * @param {Integer} taillePolice : Indice de la taiile de police utilisée
 * @param {Boolean} encadrement : true si encadré.
 * @param {Boolean} effacementFond : true si on efface le fond de l'en-tête.
 * @param {mtg32.Color} couleurFond : La couleur de fond de l'en-tête.
 * @param {mtg32.CCalcul} calculAssocie : Le calcul dont l'éditeur peut changer la formule.
 * @param {String} enTete : Ce qui est affiiché devant l'éditeur proprement dit. Encadrer par des $
 * pour obtenir un affichage LaTeX.
 * @param {Integer} nbcol : Le nombre de colonnes de l'éditeur.
 * @param {Boolean} affichageFormule : true si on affiche la formule à droite de l'éditeur.
 * @param {String} preCodeLaTeX : Donne le code LaTeX de ce qu'il faut afficher entre l'éditeur
 * et l'affichage de la formule quand cet affichage est demandé.
 * @param {Boolean} variables1Car : true si on n'utilise que des calculs et variables dont les nomes sont de 1 caractère.
 * Dans ce cas les signes de multiplications sont omis dans l'affichage de formule s'il est demané.
 * @param {Boolean} affLaTeXPremierAffichage : true si la formule dit être affichée à droite de l'éditeur
 * immédiatement après le chargement de la figure.
 * @param {Boolean} affichageTempsReel : true si l'affichage de formule (en LaTeX) à droite
 * de l'éditeur doit se faire au fur  * et à mesure que l'utilisateur tape auclavier.
 * @returns {CEditeurFormule}
 */
function CEditeurFormule (listeProprietaire, impProto, estElementFinal, couleur, x_nom, y_nom,
                          dec_x, dec_y, masque, taillePolice, encadrement, effacementFond, couleurFond, calculAssocie,
                          enTete, nbcol, affichageFormule, preCodeLaTeX, variables1Car, affLaTeXPremierAffichage, affichageTempsReel) {
  if (arguments.length !== 0) {
    if (arguments.length === 1) CAffichageLieAPoint.call(this, listeProprietaire);
    else {
      CAffichageLieAPoint.call(this, listeProprietaire, impProto, estElementFinal, couleur,
        x_nom, y_nom, dec_x, dec_y, masque, null, taillePolice, encadrement, effacementFond, couleurFond,
        CAffichageLieAPoint.alignementHorizontalDroit, CAffichageLieAPoint.alignementVerticalHaut);
      this.calculAssocie = calculAssocie;
      this.enTete = enTete;
      this.nbcol = nbcol;
      this.affichageFormule = affichageFormule;
      this.preCodeLaTeX = preCodeLaTeX;
      this.variables1Car = variables1Car;
      this.affLaTeXPremierAffichage = affLaTeXPremierAffichage;
      this.affichageTempsReel = affichageTempsReel;
    }
    this.indErr = new Pointeur(0);
  }
  this.callBackOK = null; // Ajout version 4.9.3
}
CEditeurFormule.prototype = new CAffichageLieAPoint();
CEditeurFormule.prototype.constructor = CEditeurFormule;
CEditeurFormule.prototype.superClass = "CAffichageLieAPoint";
CEditeurFormule.prototype.className = "CEditeurFormule";

CEditeurFormule.prototype.numeroVersion = function() {
  return 4;
};

CEditeurFormule.prototype.getClone = function(listeSource, listeCible) {
  var ind1 = listeSource.indexOf(this.calculAssocie);
  var ind3 = listeSource.indexOf(this.impProto);
  return new CEditeurFormule(listeCible, listeCible.get(ind3, "CImplementationProto"),
    this.estElementFinal, this.couleur, this.x_nom, this.y_nom, this.dec_x, this.dec_y, this.masque,
    this.taillePolice, this.encadrement, this.effacementFond, this.couleurFond,
    listeCible.get(ind1, "CCalcul"), this.enTete, this.nbcol, this.affichageFormule, this.preCodeLaTeX,
    this.variables1Car, this.affLaTeXPremierAffichage, this.affichageTempsReel);
};

CEditeurFormule.prototype.dependDe = function(p) {
  if (this.elementTestePourDependDe === p) return this.dependDeElementTeste;
  return this.memoriseDependance(CAffichageLieAPoint.prototype.dependDe.call(this, p) || this.calculAssocie.dependDe(p));
};

CEditeurFormule.prototype.dependDePourBoucle = function(p) {
  return CAffichageLieAPoint.prototype.dependDePourBoucle.call(this, p) || this.calculAssocie.dependDePourBoucle(p);
};
/**
 * Fournit la chaîne représentant l'en-tête à afficher devant l'éditeur proprement dit
 * @returns {String}
 */
CEditeurFormule.prototype.rendChaineAffichage = function() {
  var i;
  if (this.enTete.length === 0) {
    var ch = this.calculAssocie.nomCalcul;
    if (this.calculAssocie.estDeNatureCalcul(Nat.or(NatCal.NFonction , NatCal.NFonctionComplexe))) {
      // CFonc fonc = (CFonc) calculAssocie;
      ch = ch + CCb.parouv + this.calculAssocie.chaineVariable + CCb.parfer;
    }
    else {
      if (this.calculAssocie.estDeNatureCalcul(NatCal.NTteFoncRouC)) {
        // CFoncNVar fonc = (CFoncNVar) calculAssocie;
        ch = ch + "(";
        for (i = 0; i < this.calculAssocie.nbVar; i++) {
          ch = ch + this.calculAssocie.chaineVariable[i];
          if (i !== this.calculAssocie.nbVar - 1) ch = ch + ',';
        }
        ch = ch + ")";
      }
    }
    // if (!this.affichageFormule) ch = ch + " = ";
    // else ch = ch + "\\;";
    ch = ch + " = ";
    // Modification version 4.7.2 : Affichage par défaut en LaTeX de l'en-tête
    ch = "$" + ch + "$";
    return ch;
  }
  else return this.enTete;
};

CEditeurFormule.prototype.getNature = function() {
  return NatObj.NEditeurFormule;
};

CEditeurFormule.prototype.montre = function() {
  CAffichageLieAPoint.prototype.montre.call(this);
  this.divi.style.visibility = "visible";
  if (this.affichageFormule) {
    if (this.glatex === undefined) {
      this.glatex = this.latex.createg();
      var listePr = this.listeProprietaire;
      var svg = document.getElementById(listePr.id);
      svg.appendChild(this.glatex);
    }
    this.glatex.setAttribute("visibility", "visible");
  }
};

CEditeurFormule.prototype.cache = function() {
  CAffichageLieAPoint.prototype.cache.call(this);
  this.divi.style.visibility = "hidden";
  if (this.affichageFormule && this.glatex) this.glatex.setAttribute("visibility", "hidden");
};

/**
 * Fonction appelée pour l'événement onblur de l'éditeur.
 * Attention this pointe sur le composant
 * @returns {undefined}
 */
CEditeurFormule.prototype.onblur = function() {
  var b = this.owner.validation(true);
  if (b) this.owner.demarquePourErreur();
  else this.owner.marquePourErreur();
};
/**
 * Fonction appelée par l'événement onkeyup de l'éditeur
 * Attention this pointe sur le composant
 * @param {Event} evt
 * @returns {undefined}
 */
CEditeurFormule.prototype.onkeyup = function(evt) {
  // Non traité si c'est la touche entrée ou une touche de déplacement de curseur
  var kc = evt.keyCode;
  // On sort si touche entrée ou déplacement à gauche ou à droite
  if ((evt.charCode===0) && ((kc === 13)||(kc === 37) || (kc === 39))) return;
  // On regarde si un set de caractères autorisés a été spécifié :
  var owner = this.owner;
  var listePr = owner.listeProprietaire;
  var svg = document.getElementById(listePr.id);
  var dimf = new Dimf(parseInt(svg.getAttribute("width")), parseInt(svg.getAttribute("height")));
  // var doc = listePr.documentProprietaire;
  var chCalcul = owner.variables1Car ? listePr.traiteSignesMult(this.value):this.value;
  var indErr = owner.indErr;

  var calculAssocie = owner.calculAssocie;
  // On autorise d'utiliser un calcul non créé par l'utilisateur
  var indDernier = listePr.indexOf(calculAssocie) - 1;
  var varFor = calculAssocie.variableFormelle();
  var bcomplexe = calculAssocie.estDeNatureCalcul(Nat.or(NatCal.NCalculComplexe,
    NatCal.NFonctionComplexe, NatCal.NFonctionComplexe2Var, NatCal.NFonctionComplexe3Var));
  if (bcomplexe ? CalcC.verifieSyntaxeComplexe(listePr, chCalcul, indErr,
        indDernier, varFor) : CalcR.verifieSyntaxe(listePr, chCalcul, indErr, indDernier, varFor)) {
    owner.demarquePourErreur();
  }
  if (owner.affichageFormule && owner.affichageTempsReel) {
    var calc = bcomplexe ? CalcC.ccbComp(chCalcul ,listePr, 0,
        chCalcul.length-1, varFor) : CalcR.ccb(chCalcul ,listePr, 0, chCalcul.length-1, varFor);
    // try rajouté version 4.7.3
    // cadre.repaintFigureSansCalcul();

    owner.latex.chaineCommentaire = owner.preCodeLaTeX + " " + calc.chaineLatexSansPar(varFor);
    owner.latex.positionne(false, dimf);
    owner.latex.setReady4MathJax();
    /*
     var newglatex = owner.latex.createg();
     svg.replaceChild(newglatex, owner.glatex);
     owner.glatex = newglatex;
     */
    MathJax.Hub.Queue([owner.callBack,owner,svg]);
  }
};
/**
 * Fonction appelée par l'événement onkeypress de l'éditeur
 * Attention this pointe sur le composant
 * @param {Event} evt
 * @returns {undefined}
 */
CEditeurFormule.prototype.onkeypress = function(evt) {
  var i,edit,ind,ch,owner,s,b;
  var self = this;
  owner = this.owner;
  if (evt.keyCode === 13) {
    evt.preventDefault(); // Pour compatibilité avec j3p
    // evt.stopPropagation(); // Pour compatibilité avec j3p
    b = owner.validation(true);
    if (b) owner.demarquePourErreur();
    else owner.marquePourErreur();
    // Ajout version 4.9.3
    if ((owner.callBackOK !== null) && b && (owner.editeur.value.length !== 0))  owner.callBackOK.call();
    //  evt.preventDefault(); // Supprimé version 5.2 pour que le clavier des périphériques mobile se replie
    // Sinon avec clavier on garde le focus avec le point d'insertion à l'endroit de la faute
    // Ci-dessous nécessaire d'utiliser la queue de MathJax car sinon on blur peut appeler la fonction
    // validation() trop tôt
    if (!owner.listeProprietaire.documentProprietaire.hasMouse) {
      MathJax.Hub.Queue(function() {self.blur()});
    }
    return;
  }
  // On regarde si un set de caractères autorisés a été spécifié :
  if (owner.charset && owner.charset !== "") {
    if ((evt.charCode !== 0) && owner.charset.indexOf(String.fromCharCode(evt.charCode)) === -1) {
      evt.preventDefault();
      return;
    }
  }
  if (evt.charCode === 40) { // On tape une parenthèse ouvrante
    // Si l'expression est correcte au niveau des parenthèses et si le curseur n'est suivi
    // que d'espaces ou de parenthèses fermantes, on rajoute une parenthèse fermante
    edit = owner.editeur;
    ind = edit.selectionStart;
    ch = edit.value;
    s = 0;
    for (i = 0; i < ch.length; i++) {
      if (ch.charAt(i) === "(") s++;
      else if (ch.charAt(i) === ")") s--;
    }
    b = true;
    for (i = ind; i<ch.length; i++) {
      if ((ch.charAt(i) !== ")") || (ch.charAt(i) !== ")")) b = false;
    }
    if ((s===0) && b) {
      edit.value = edit.value.substring(0,ind)+ "()" + edit.value.substring(ind);
      edit.selectionStart = ind+1;
      edit.selectionEnd = ind+1;
      evt.preventDefault();
    }
  }
};
/**
 * Fonction de callBack appelée pour mettre à jour l'affichage LaTeX de
 * la formule si celui-ci est activé..
 * Lors de l'applet owner pointe sur this.
 * @param {CEditeurFormule} owner
 * @param {svg} svg
 * @returns {undefined}
 */
CEditeurFormule.prototype.callBack = function(owner, svg) {
  var newglatex = owner.latex.createg();
  svg.replaceChild(newglatex, owner.glatex);
  owner.glatex = newglatex;
};
/**
 * Fonction encadrant l'éditeur de rouge en cas de faute de syntaxe
 * @returns {undefined}
 */
CEditeurFormule.prototype.marquePourErreur = function() {
  // this.form.style.border = "1px solid red"; // Modifié version mtgApp
  this.editeur.style["background-color"] = "#FF9999";
  this.editeur.setSelectionRange(this.indErr.getValue(),this.indErr.getValue());
};
/**
 * Fonction retirant l'éventuel cadre rouge encadrant l'éditeur en cas de
 * faute de syntaxe.
 * @returns {undefined}
 */
CEditeurFormule.prototype.demarquePourErreur = function() {
  // this.form.style.border = "none" // Modifié version mtgApp
  this.editeur.style["background-color"] = "#FFFFFF";
};
/**
 * Fonction créat un div contenant un formulaire et à l'intérieur de celui-ci
 * un éditeur input contenant l'éditeur de formule.
 * Ce div est rajouté au div propriétaire du svg contenant la figure.
 * @returns {undefined}
 */
CEditeurFormule.prototype.creeEditeur = function() {
  var svg,par,id;
  // Modification version mtgApp : Le parent du svg de la figure est un svg. Il faudra tenir compte
  // de son décalage par rapport au div parnet
  id = this.listeProprietaire.id;
  if (id === null) return; // Pour les figures servant seulement pour leurs objets de type calcul
  this.divi = kernel.ce("div");
  this.editeur = kernel.ce("input");

  // this.editeur.onchange = this.onchange;
  this.editeur.onblur = this.onblur;

  this.editeur.owner = this;
  // this.form.onsubmit = this.onsubmit;
  if (this.affichageTempsReel) {
    this.editeur.onkeyup = this.onkeyup;
  }
  this.editeur.onkeypress = this.onkeypress;
  this.divi.appendChild(this.editeur);
  this.editeur.setAttribute("type","text");
  this.editeur.setAttribute("size", this.nbcol);
  // this.editeur.setAttribute("style","fontSize:"+Fonte.taille(this.taillePolice)+"px;")
  this.editeur.style.fontSize =this.taillePolice+"px";
  this.editeur.style.fontFamily = "TNR";
  this.editeur.id = id + this.calculAssocie.nomCalcul; // Por les échanges avec J3P
  /*
   var bb = document.getElementById(this.listeProprietaire.id).getBoundingClientRect();
   x = bb.left + this.x_nom + this.dec_x;
   y = bb.top + this.y_nom + this.dec_y - 2;
   */
  svg = document.getElementById(id);
  this.svg = svg; // On mémorise le svg pour que dans deleteComponent() on puisse retire le div qui conteient l'éditeur
  // Modification version mtgApp : En utilisant les onglets dans mathematice, il y avait un bug
  // En effet si le div parent n'est pas visible, this.div.cleintHeight par exemple renvoie 0
  // On attache donc d'abord le div au document.body, puis on le retire pour le réattacher ensuite au div parent
  document.body.appendChild(this.divi);

  /* Modification version WebPack
   //
   // var pos = mtg32.getPosition2FirstNonStatic(par);
   // x = pos.x + this.x_nom + this.dec_x + 1; Modifé version 4.9.2
   // x = this.x_nom + this.dec_x + 2; Modifié version mtgApp
   x = decx + this.x_nom + this.dec_x + 2;
   // y = pos.y + this.y_nom + this.dec_y - 2; // Modifié pour J3P
   // y = pos.y + this.y_nom + this.dec_y - 6 + this.editeur.clientHeight/2; // Modifié pour J3P
   // Remodifié le &5-5-2014
   // y = this.y_nom + this.dec_y - 4 + this.editeur.clientHeight/2; Modifié version MtgApp
   y = decy + this.y_nom + this.dec_y - 4 + this.editeur.clientHeight/2;
   // x = this.x_nom + this.dec_x;
   // y = this.y_nom + this.dec_y - 2;
   if (par.style.position === "") par.style.position = "relative";
   var style= "left:"+x+"px;"+"top:"+y+"px;"+"position:absolute;margin:0px;visibility:"+(this.masque ? "hidden;" : "visible;");
   // var style= "left:"+x+"px;"+"top:"+y+"px;" + "margin:0px;";
   this.divi.setAttribute("style", style);
   */
  this.placeEditeur();
  if (this.affichageFormule) {
    this.latex = new CLatex(this.listeProprietaire, null, false, this.couleur,
      this.x_nom + this.divi.clientWidth + 3, this.y_nom + this.taillePolice/2 + 2, this.dec_x, this.dec_y, false,
      null, this.taillePolice, StyleEncadrement.Sans, this.effacementFond, this.couleurFond,
      CAffichageLieAPoint.alignementHorizontalGauche, CAffichageLieAPoint.alignementVerticalCentre,
      this.preCodeLaTeX + " " + this.calculAssocie.calcul.chaineLatexSansPar(this.calculAssocie.variableFormelle()));
    // Ligne suivante ajoutée version WebBack pour pouvoir déplacer un éditeur avec l'outil de capture
    // Utilisé dans CEditeurFormule.placeNom()
    this.latex.owner = this;
  }
  document.body.removeChild(this.divi);
  par = svg.parentNode; // Le parent du svg doit être un div
  // Modification version mtgApp : Dans le cas d'une mtgApp, le parent du svg de la figure est un autre svg
  // et c'est son div parent qu'il faut utiliser
  if (par.tagName .toLowerCase() !== "div") par = par.parentNode;
  // Modifié version 4.9.9.3
  par.appendChild(this.divi); //On attache l'éditeur au div parent du svg

};

CEditeurFormule.prototype.placeEditeur = function() {
  var svg, par, style, x, y
  var decx = 0;
  var decy = 0;
  svg = document.getElementById(this.listeProprietaire.id);
  par = svg.parentNode; // Le parent du svg doit être un div
  // Modification version mtgApp : Dans le cas d'une mtgApp, le parent du svg de la figure est un autre svg
  // et c'est son div parent qu'il faut utiliser
  if (par.tagName .toLowerCase() !== "div") {
    decx = parseInt(svg.getAttribute("x"));
    decy = parseInt(svg.getAttribute("y"));
    // par = par.parentNode;
  }
  //
  // var pos = mtg32.getPosition2FirstNonStatic(par);
  // x = pos.x + this.x_nom + this.dec_x + 1; Modifé version 4.9.2
  // x = this.x_nom + this.dec_x + 2; Modifié version mtgApp
  x = decx + this.x_nom + this.dec_x + 2;
  // y = pos.y + this.y_nom + this.dec_y - 2; // Modifié pour J3P
  // y = pos.y + this.y_nom + this.dec_y - 6 + this.editeur.clientHeight/2; // Modifié pour J3P
  // Remodifié le &5-5-2014
  // y = this.y_nom + this.dec_y - 4 + this.editeur.clientHeight/2; Modifié version MtgApp
  // y = decy + this.y_nom + this.dec_y - 4 + this.editeur.clientHeight/2;
  // Modification version WebPack
  y = decy + this.y_nom + this.dec_y + this.taillePolice/2 - this.editeur.clientHeight/2 - 1;
  // x = this.x_nom + this.dec_x;
  // y = this.y_nom + this.dec_y - 2;
  // if (par.style.position === "") par.style.position = "relative"; // Déplacé dans l'appli version mtgApp
  style= "left:"+x+"px;"+"top:"+y+"px;"+"position:absolute;margin:0px;visibility:"+(this.masque ? "hidden;" : "visible;");
  // var style= "left:"+x+"px;"+"top:"+y+"px;" + "margin:0px;";
  this.divi.setAttribute("style", style);
  // Modifié version 4.9.9.3

}
/**
 * Fonction retirant le div contenant le composant formulaire contenant le input
 */
CEditeurFormule.prototype.deleteComponent = function() {
  try {
    //this.svg a été établit lors de l'appel à creeEditeur
    var par = this.svg.parentNode;
    // Modification version WepPack. Dans cette version le parent peut être un svg
    if (par.tagName .toLowerCase() !== "div") par = par.parentNode;
    par.removeChild(this.divi);
  }
  catch (ex) {}
};

// Ajout version webPack
CEditeurFormule.prototype.createComponent = function() {
  this.creeEditeur();
}

CEditeurFormule.prototype.donneCouleur = function(color) {
  CAffichageLieAPoint.prototype.donneCouleur.call(this, color);
  if (this.affichageFormule) this.latex.donneCouleur(color);
}

CEditeurFormule.prototype.setReady4MathJax = function() {
  CAffichageLieAPoint.prototype.setReady4MathJax.call(this);
  if (this.affichageFormule) this.latex.setReady4MathJax();
};

CEditeurFormule.prototype.setReady4MathJaxUpdate = function() {
  CAffichageLieAPoint.prototype.setReady4MathJaxUpdate.call(this);
  if (this.affichageFormule) {
    this.latex.setReady4MathJax(); // Spécial pour cet objet
  }
};
/**
 * Fonction mettant dans l'éditeur la chaîne de caractères st et mettant à jour
 * en conséquence l'affichage de formule LaTeX s'il est activé.
 * @param {type} st
 * @returns {undefined}
 */
CEditeurFormule.prototype.setEditorValue = function(st) {
  this.editeur.value = st;
  var liste = this.listeProprietaire;
  var ch = this.variables1Car ? liste.traiteSignesMult(st):st;
  var svg = document.getElementById(liste.id);
  var dimf = new Dimf(parseInt(svg.getAttribute("width")), parseInt(svg.getAttribute("height")));
  var varFor = this.calculAssocie.variableFormelle();
  var bcomplexe = this.calculAssocie.estDeNatureCalcul(Nat.or(NatCal.NCalculComplexe,
    NatCal.NFonctionComplexe, NatCal.NFonctionComplexe2Var, NatCal.NFonctionComplexe3Var));
  if (this.affichageFormule) {
    var calc = bcomplexe ? CalcC.ccbComp(ch ,liste, 0,
        ch.length-1, varFor) : CalcR.ccb(ch ,liste, 0, ch.length-1, varFor);
    this.latex.chaineCommentaire = this.preCodeLaTeX + " " + calc.chaineLatexSansPar(varFor);
    this.latex.positionne(false, dimf);
    this.latex.setReady4MathJax();
    MathJax.Hub.Queue([this.callBack,this,svg]);
  }
};

CEditeurFormule.prototype.positionne = function(infoRandom, dimfen) {
  CAffichageLieAPoint.prototype.positionne.call(this,infoRandom, dimfen);
  if (this.affichageFormule) this.latex.positionne(infoRandom, dimfen);
};

CEditeurFormule.prototype.trace = function(svg, couleurFond, transparence) {
  CAffichageLieAPoint.prototype.trace.call(this, svg, couleurFond, transparence);
  if (this.affichageFormule) {
    // this.latex.positionne();
    this.glatex = this.latex.createg();
    if (!this.affLaTeXPremierAffichage) this.glatex.setAttribute("visibility", "hidden");
    svg.appendChild(this.glatex);
  }
};
/**
 * Fonction applelé pour valider ou non sur le plan syntaxique le contenu de l'éditeur.
 * @param {Boolean} redraw : Di true on réactualise l'affichage de la formule.
 * @returns {Boolean}
 */
CEditeurFormule.prototype.validation = function(redraw) {
  var txt,listePr,calculAssocie,chCalcul,indDernier,varFor,bcomplexe,v,calc;
  listePr = this.listeProprietaire;
  txt = this.editeur.value;
  if (txt.length === 0) return true;
  // String chCalcul = getText(); // Modifié version 4.6
  chCalcul = this.variables1Car ? listePr.traiteSignesMult(txt) : txt;
  // Spécial version Applet !
  calculAssocie = this.calculAssocie;
  // On autorise d'utiliser un calcul non créé par l'utilisateur
  indDernier = listePr.indexOf(calculAssocie) - 1;
  varFor = calculAssocie.variableFormelle();
  bcomplexe = calculAssocie.estDeNatureCalcul(Nat.or(NatCal.NCalculComplexe,
    NatCal.NFonctionComplexe, NatCal.NFonctionComplexe2Var, NatCal.NFonctionComplexe3Var));
  if (bcomplexe ? CalcC.verifieSyntaxeComplexe(listePr, chCalcul, this.indErr,
        indDernier, varFor) : CalcR.verifieSyntaxe(listePr, chCalcul, this.indErr,
        indDernier, varFor)) {
    calc = bcomplexe ? CalcC.ccbComp(chCalcul ,listePr, 0,
        chCalcul.length-1, varFor) : CalcR.ccb(chCalcul ,listePr, 0,
        chCalcul.length-1, varFor);
    if (calculAssocie.estDeNatureCalcul(Nat.or(NatCal.NCalculReel, NatCal.NCalculComplexe))) {
      try {
        calc.listeProprietaire.initialiseNombreIterations();
        if (bcomplexe) {
          v = new Complexe();
          calc.resultatComplexe(false, v);
        }
        else {
          calc.resultat(false);
        }
      }
      catch (e) {
        this.indErr.setValue(txt.length);
        return false;
      }
    }
    calculAssocie.calcul = calc;
    // Modification version 4.5.2 : Quand la chaîne de calcul sera reconstruite elle pourra être différente de celle ici entrée
    // On la change tout de suite
    calculAssocie.chaineCalcul = calculAssocie.calcul.chaineCalculSansPar(varFor);
    //
    // Modification bug version 4.7.1 : On remet à jour les objets dépendant, en particulier les calculs de dérivées
    // Modifié version 4.7.2
    // Modifié version 4.7.3
    listePr.metAJourObjetsDependantDeSaufEditeursFormule(calculAssocie);
    // On recalcule la figure et on la réaffiche
    if (redraw) {
      var svg = document.getElementById(listePr.id);
      var dimf = new Dimf(parseInt(svg.getAttribute("width")), parseInt(svg.getAttribute("height")));
      // listePr.positionne(false, dimf);
      var doc = listePr.documentProprietaire;
      // Ajout version 5.2. Indispensable
      // listePr.setReady4MathJaxUpdate();
      //
      // listePr.update(svg, doc.couleurFond, doc.opacity, true);
      listePr.positionneDependantsDe(false, dimf, calculAssocie);
      listePr.updateDependants(calculAssocie, svg, doc.couleurFond, doc.opacity, true);

      if (this.affichageFormule) {
        this.latex.chaineCommentaire = this.preCodeLaTeX + " " + this.calculAssocie.calcul.chaineLatexSansPar(varFor);
        this.latex.positionne(false, dimf);
        // Modification version 4.9.2
        this.latex.setReady4MathJax();
        var t = this;
        MathJax.Hub.Queue([this.callBack,t,svg]);
      }
    }
    return true;
  }
  else {
    this.adapteIndiceErreur();
    return false;
  }
};
/**
 * Fonction renvoyant true si car est une chaine de un caractère contenant soit
 * un chiffre soit un caractère ²
 * @param {String} car
 * @returns {Boolean}
 */
CEditeurFormule.prototype.chiffreOuCarre = function(car) {
  return ((car === '²') || Fonte.chiffre(car));
};
/**
 * Fonction renvoyant true si car est une chaine de un caractère contenant soit
 * un chiffre soit le point décimal .
 * @param {String} car
 * @returns {Boolean}
 */
CEditeurFormule.prototype.chiffreOuPointDecimal = function(car) {
  return ((car === '.') || Fonte.chiffre(car));
};
/**
 * Fonction renvoyant true si car est une chaine de un caractère contenant soit
 * une lettre soit un caractère ' ou "
 * @param {String} car
 * @returns {Boolean}
 */
CEditeurFormule.prototype.lettreOuPrime = function(car) {
  return (Fonte.lettre(car) || (car === '\'') || (car === '\"'));
};
/**
 * Fonction renvoyant true si pChaine commence à partir de l'indice indsart
 * soit par le nom d'une fonction prédéfinie suivi d'une parenthèse ouvrante.
 * @param {String} pChaine
 * @param {Integer} indstart
 * @param {Pointeur} longNom : Au retour longNom.getValue() renvoie la longueur
 * du nom de la fonction si la recherche a été positive.
 * @returns {Boolean}
 */
CEditeurFormule.prototype.commenceParNomFonctionPredefinieSuivieParOuvr = function(pChaine, indstart, longNom) {
  var ch = pChaine.substring(indstart).toLowerCase();
  var i;
  // On commence par les fonctions de quatre variables car integrale commence par int une autre fonction
  for (i = 0; i < Opef4v.nomsFoncs4Var.length; i++) {
    longNom.setValue(Opef4v.nomsFonctions4Var(i).length);
    if (ch.indexOf(Opef4v.nomsFonctions4Var(i).toLowerCase()+"(") === 0) return true;
  }
  for (i = 0; i < Opef.nomsFoncs.length; i++) {
    longNom.setValue(Opef.nomsFonctions(i).length);
    if (ch.indexOf(Opef.nomsFonctions(i).toLowerCase()+"(") === 0) return true;
  }
  for (i = 0; i < Opef2v.nomsFoncs2Var.length; i++) {
    longNom.setValue(Opef2v.nomsFonctions2Var(i).length);
    if (ch.indexOf(Opef2v.nomsFonctions2Var(i).toLowerCase()+"(") === 0) return true;
  }
  for (i = 0; i < Opef3v.nomsFoncs3Var.length; i++) {
    longNom.setValue(Opef3v.nomsFonctions3Var(i).length);
    if (ch.indexOf(Opef3v.nomsFonctions3Var(i).toLowerCase()+"(") === 0) return true;
  }
  for (i = 0; i < Opef5v.nomsFoncs5Var.length; i++) {
    longNom.setValue(Opef5v.nomsFonctions5Var(i).length);
    if (ch.indexOf(Opef5v.nomsFonctions5Var(i).toLowerCase()+"(") === 0) return true;
  }
  // Inutile de regarder les noms des fonctions complexes de plus de deux variables car c'est le même que les fonctions réelles
  for (i = 0; i < Opef.nomsFoncsComplexes.length; i++) {
    longNom.setValue(Opef.nomsFonctionsComplexes(i).length);
    if (ch.indexOf(Opef.nomsFonctionsComplexes(i).toLowerCase()+"(") === 0)  return true;
  }
  return false;
};
/**
 * Fonction renvoyant true si, à partir de l'indice indstart, pChaine commence
 * par un nom de fonction prédéfinie suivi d'une parenthèse ouvrante.
 * @param {String} pChaine : La chaîne analysée.
 * @param {Integer} indstart : L'indice du début de la recherche.
 * @param {Integer} longNom : Renvoie par getValue() la longueur du nom de la fonction
 * si la recherche a été positive.
 * @returns {Boolean}
 */
CEditeurFormule.prototype.commenceParNomFonctionUtilisateurSuivieParOuvr = function(pChaine, indstart, longNom) {
  var i,elb,nom;
  var liste = this.listeProprietaire;
  var ch = pChaine.substring(indstart).toLowerCase();

  for (i = 0; i < liste.longueur(); i++) {
    elb = liste.get(i);
    if (!elb.estElementIntermediaire()
      && elb.estDeNatureCalcul(Nat.or(NatCal.NTteFoncR,
        NatCal.NDerivee, NatCal.NSuiteRecurrenteReelle, NatCal.NTteFoncC,
        NatCal.NSuiteRecurrenteComplexe))) {
      nom = elb.getNom();
      if (ch.indexOf(nom + "(") === 0) {
        longNom.setValue(nom.length);
        return true;
      }
    }
  }
  return false;
};
/**
 * Fonction mettant, dans le cas d'une erreur de syntaxe, dans this .indErr l'indice
 * de l'erreur dans la chaîne de caractères de l'éditeur, dans le cas où
 * les signes de multiplication implicites sont utilisés.
 * @returns {undefined}
 */
CEditeurFormule.prototype.adapteIndiceErreur = function() {
  var lon = new Pointeur(0);
  if (this.variables1Car) {
    var ch = this.editeur.value;
    var carprecedent = '(';
    var k = 0; // Représente l'indice dans la chaîne à laquelle ont été rajoutés des éventuels signs de multiplication
    var i = 0; // Reprsente l'indice dans la chaîe qui a été entrée avec des multiplications implicites.
    var len = ch.length;
    while ((k <= this.indErr.getValue()) && (i < len)) {
      var car = ch.charAt(i);
      if (this.chiffreOuPointDecimal(car) || (car === ')') || (car === '+') || (car === '-')
        || (car === '*') || (car === '/') || (car === '^') || (car === '\'') || (car === '\"') || (car === '²')) {
        carprecedent = car;
        i++;
        k++;
      }
      else {
        if (ch.indexOf("pi",i) === 0) {
          k += 2;
          if (!(this.chiffreOuPointDecimal(carprecedent) || lettreOuPrime(carprecedent) || (carprecedent === ')'))) k++;
          carprecedent = ')';
          i += 2;
        }
        else {
          if (this.commenceParNomFonctionPredefinieSuivieParOuvr(ch, i, lon)) {
            k += lon.getValue();
            if (this.chiffreOuPointDecimal(carprecedent) || this.lettreOuPrime(carprecedent) || (carprecedent === ')')) k++;
            i+= lon.getValue();
            carprecedent = '(';
          }
          else {
            if (this.commenceParNomFonctionUtilisateurSuivieParOuvr(ch, i, lon)) {
              k += lon.getValue();
              if (this.chiffreOuPointDecimal(carprecedent) || this.lettreOuPrime(carprecedent) || (carprecedent === ')')) k++;
              i+= lon.getValue();
              carprecedent = '(';
            }
            else {
              k++;
              i++;
              // Si lettre suivie d'une autre lettre
              if (this.lettreOuPrime(carprecedent) && Fonte.lettre(car)) k++;
              // Si chiffre suivi d'une lettre
              else if (this.chiffreOuCarre(carprecedent) && Fonte.lettre(car)) k++;
              // Si chiffre ou lettre suivi d'une parenthèse on rajoute un signe de multiplication
              else if ((this.chiffreOuCarre(carprecedent) || this.lettreOuPrime(carprecedent)) && (car === '(')) k++;
              // Si parenthèse fermante suivie d'un chiffre ou une lettre ou une parenthèse ouvrante on rajoute un signe de multiplication
              else if ((carprecedent === ')') && (Fonte.lettre(car) || Fonte.chiffre(car) || (car === '('))) k++;
              carprecedent = car;
            }
          }
        }
      }
    }
    this.indErr.setValue(i);
  }
};

CEditeurFormule.prototype.chaineDesignation = function() {
  return "desEditeur";
};

CEditeurFormule.prototype.read = function(inps, list) {
  CAffichageLieAPoint.prototype.read.call(this, inps, list);
  var ind1 = inps.readInt();
  this.calculAssocie = list.get(ind1, "CFonc");
  this.enTete = CLatex.traiteAccents(inps.readUTF());
  this.nbcol = inps.readInt();
  this.variables1Car = inps.readBoolean();
  this.affichageFormule = inps.readBoolean();
  this.preCodeLaTeX = inps.readUTF();
  this.affLaTeXPremierAffichage = inps.readBoolean();
  this.affichageTempsReel = inps.readBoolean();
  // A revoir JavaScript
  /*
   if (listeProprietaire.bCreationComposants) {
   mtgFormulaEditor = new MtgFormulaEditor(this);
   mtgFormulaEditor.setVisible(!masque);
   mtgFormulaEditor.setForeground(couleur);
   }
   */
  // Ajout version 4.7.1
  if (list.className !== "CPrototype") this.creeEditeur();
  // Ajout version 4.9.3
  this.callBackOK = null;
};

CEditeurFormule.prototype.write = function(oups, list) {
  CAffichageLieAPoint.prototype.write.call(this, oups, list);
  var ind1 = list.indexOf(this.calculAssocie);
  oups.writeInt(ind1);
  oups.writeUTF(this.enTete);
  oups.writeInt(this.nbcol);
  oups.writeBoolean(this.variables1Car);
  // Ajout version 4.7.2
  oups.writeBoolean(this.affichageFormule);
  oups.writeUTF(this.preCodeLaTeX);
  oups.writeBoolean(this.affLaTeXPremierAffichage);
  oups.writeBoolean(this.affichageTempsReel);
};

/**
 * Fonction à appeler depuis un autre code JavaScript.
 * Quant l'utilisateur validera sa formule dans l'éditeur
 * par la touche Entée, a fonction f possédée par le code appelant sera allors appelée.
 * @param {type} f
 * @returns {undefined}
 */
CEditeurFormule.prototype.setCallBackOK = function(f) {
  this.callBackOK = f;
};


// WEBPACK FOOTER //
// ./src/objets/CEditeurFormule.js